/**
 * landingTheme(slug, lang) — le contenu d'une landing thématique dans la
 * langue demandée.
 *
 * Le français reste la BASE : `landings.js` porte l'intégralité d'un thème,
 * texte et données de présentation mêlés (score, teinte de l'anneau, largeur
 * des barres de projection, drapeau `weak`…). Les fichiers de traduction ne
 * portent QUE du texte, et sont superposés à cette base.
 *
 * Pourquoi une superposition plutôt que trois copies complètes : les
 * chiffres d'un thème — le score sur 100, les montants, les pourcentages de
 * projection — sont les mêmes dans les trois langues. Les recopier trois
 * fois, c'est garantir qu'un jour l'anglais affiche 78 là où le français
 * affiche 75, sans que personne ne s'en aperçoive. Ici il n'existe qu'un
 * seul endroit où un chiffre est écrit.
 *
 * Un thème sans traduction retombe donc entièrement sur le français, et un
 * thème partiellement traduit ne perd que les champs manquants : la page
 * s'affiche toujours, jamais un trou.
 */
import { LANDING_THEMES } from './landings';
import { LANDING_THEMES_NL } from './landings.nl';
import { LANDING_THEMES_EN } from './landings.en';

const OVERLAYS = { nl: LANDING_THEMES_NL, en: LANDING_THEMES_EN };

/**
 * Superpose `over` sur `base`, récursivement.
 *  · objets  → fusion clé à clé ;
 *  · tableaux → fusion PAR INDICE, ce qui permet de ne traduire que le
 *    `label` d'un KPI en laissant sa couleur et son `kind` venir de la base ;
 *  · scalaires → `over` gagne quand il est défini.
 * Un élément de tableau absent côté traduction garde celui de la base.
 */
function overlay(base, over) {
  if (over === undefined || over === null) return base;
  if (Array.isArray(base)) {
    if (!Array.isArray(over)) return over;
    return base.map((item, i) => overlay(item, over[i]));
  }
  if (base && typeof base === 'object') {
    if (typeof over !== 'object' || Array.isArray(over)) return over;
    const out = { ...base };
    for (const k of Object.keys(over)) out[k] = overlay(base[k], over[k]);
    return out;
  }
  return over;
}

/** Le thème `slug` dans la langue `lang`, ou `undefined` si le slug n'existe pas. */
export function landingTheme(slug, lang) {
  const base = LANDING_THEMES[slug];
  if (!base) return undefined;
  const over = OVERLAYS[String(lang || 'fr').toLowerCase()];
  return over && over[slug] ? overlay(base, over[slug]) : base;
}

export default landingTheme;
